import React, { useState } from 'react';
import PackingSlip from './PackingSlip';
import ShippingLabelEditModal from './ShippingLabelEditModal';
import { openPrintDialog } from '../../utils/printUtils';
import { PACKAGE_FEATURES, isFeatureEnabled } from '../../data/packageModels';
import { savePrintHistory, PRINT_TYPES, TEMPLATE_TYPES } from '../../utils/printHistoryUtils';
import { useAuth } from '../../context/AuthContext';

/**
 * Modal สำหรับพิมพ์ใบปะหน้าออเดอร์ (Packing Slip)
 * สำหรับตรวจสอบความถูกต้องในการเตรียมจัดส่ง
 * รองรับทั้งออเดอร์เดียว (order) และหลายออเดอร์ (orders array)
 */
const PackingSlipPrintModal = ({ 
  isOpen, 
  onClose, 
  order, 
  orders, // หลายออเดอร์
  store,
  packageType 
}) => {
  const { currentUser } = useAuth();
  const [showEditModal, setShowEditModal] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('a4');
  const [savingHistory, setSavingHistory] = useState(false);

  if (!isOpen) return null;

  // ตรวจสอบว่ามีหลายออเดอร์หรือไม่
  const isMultipleOrders = Array.isArray(orders) && orders.length > 0;
  const ordersList = isMultipleOrders ? orders : (order ? [order] : []);

  // ตรวจสอบว่า package รองรับ shipping label หรือไม่
  const canPrint = isFeatureEnabled(packageType, PACKAGE_FEATURES.SHIPPING_LABEL);
  
  if (!canPrint) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-md w-full p-6">
          <h3 className="text-lg font-semibold mb-4">⚠️ ไม่สามารถพิมพ์ได้</h3>
          <p className="text-gray-600 mb-4">
            ฟีเจอร์นี้สำหรับร้านที่สมัครแพ็กเกจ Basic ขึ้นไป
          </p>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white font-semibold rounded-lg transition-colors"
          >
            ปิด
          </button>
        </div>
      </div>
    );
  }

  const handleEdit = () => {
    setShowEditModal(true);
  };

  const handleSaveEdit = (data) => {
    setEditedData(data);
    setShowEditModal(false);
  };

  const handlePrint = async () => {
    // บันทึก Print History (เฉพาะ Premium/Enterprise)
    const hasPrintHistory = isFeatureEnabled(packageType, PACKAGE_FEATURES.PRINT_HISTORY);
    if (hasPrintHistory && currentUser && store && ordersList.length > 0) {
      try {
        setSavingHistory(true);
        // บันทึก history สำหรับแต่ละออเดอร์ (ถ้ามีหลายออเดอร์)
        for (const currentOrder of ordersList) {
          await savePrintHistory({
            storeId: store.id,
            orderId: currentOrder.id,
            printType: 'packing_slip',
            templateType: selectedTemplate === 'a4' ? TEMPLATE_TYPES.A4 : 
                         selectedTemplate === 'sticker' ? TEMPLATE_TYPES.STICKER : 
                         TEMPLATE_TYPES.COMPACT,
            printData: {
              orderNumber: currentOrder.orderNumber,
              buyerName: currentOrder.buyerName,
              items: currentOrder.items,
              isMultipleOrders: isMultipleOrders,
              totalOrders: ordersList.length
            },
            printedBy: currentUser.uid,
            printedAt: new Date()
          });
        }
      } catch (error) {
        console.error('Error saving print history:', error);
      } finally {
        setSavingHistory(false);
      }
    }
    
    openPrintDialog('packing-slip-print');
  };

  const displayData = editedData || {
    buyerName: order?.buyerName || '',
    buyerPhone: order?.buyerPhone || '',
    shippingAddress: order?.shippingAddress || '',
    notes: order?.notes || ''
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white">
            <h3 className="text-lg font-semibold">
              📋 พิมพ์ใบปะหน้าออเดอร์ (Packing Slip)
              {isMultipleOrders && (
                <span className="ml-2 text-sm font-normal text-gray-600">
                  ({ordersList.length} ออเดอร์)
                </span>
              )}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-xl"
            >
              ✕
            </button>
          </div>

          <div className="p-6">
            {/* Template Selection */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                เลือก Template
              </label>
              <div className="grid grid-cols-3 gap-3">
                <button
                  onClick={() => setSelectedTemplate('a4')}
                  className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                    selectedTemplate === 'a4'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="text-sm font-semibold">A4</div>
                  <div className="text-xs text-gray-600">21x29.7cm</div>
                </button>
                <button
                  onClick={() => setSelectedTemplate('sticker')}
                  className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                    selectedTemplate === 'sticker'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="text-sm font-semibold">Sticker</div>
                  <div className="text-xs text-gray-600">10x15cm</div>
                </button>
                <button
                  onClick={() => setSelectedTemplate('compact')}
                  className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                    selectedTemplate === 'compact'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="text-sm font-semibold">Compact</div>
                  <div className="text-xs text-gray-600">8x12cm</div>
                </button>
              </div>
            </div>

            {/* Preview */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-300">
              <div className="text-sm font-semibold text-gray-700 mb-3">👁️ Preview</div>
              <div className="bg-white rounded p-4 overflow-auto" style={{ maxHeight: '500px' }}>
                <PackingSlip
                  order={order}
                  orders={orders}
                  store={store}
                  templateType={selectedTemplate}
                  editedData={editedData}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-4">
              {!isMultipleOrders && (
                <button
                  onClick={handleEdit}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                >
                  ✏️ แก้ไขข้อมูล
                </button>
              )}
              <button
                onClick={handlePrint}
                disabled={savingHistory}
                className={`${!isMultipleOrders ? 'flex-1' : 'w-full'} px-4 py-2 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {savingHistory ? '⏳ กำลังบันทึก...' : `🖨️ พิมพ์${isMultipleOrders ? ` (${ordersList.length} ออเดอร์)` : ''}`}
              </button>
            </div>

            {/* Info */}
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-xs text-blue-800">
                <strong>💡 คำแนะนำ:</strong>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>ใช้สำหรับตรวจสอบความถูกต้องในการเตรียมจัดส่ง</li>
                  <li>มี Checklist ให้ติ๊กเพื่อตรวจสอบ</li>
                  <li>มีช่องให้กรอกจำนวนที่เตรียมจริง</li>
                  <li>มีช่องลายเซ็นผู้เตรียมและผู้ตรวจสอบ</li>
                  {isMultipleOrders && (
                    <li className="font-semibold text-green-700">📊 แสดง {ordersList.length} ออเดอร์ใน 1 ใบ พร้อมยอดรวมทั้งหมด</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal - แสดงเฉพาะออเดอร์เดียว */}
      {!isMultipleOrders && (
        <ShippingLabelEditModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          order={order}
          store={store}
          onSave={handleSaveEdit}
        />
      )}
    </>
  );
};

export default PackingSlipPrintModal;

